import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Buildings, ArrowRight, DotsThreeVertical, PencilSimple, Trash } from "@phosphor-icons/react";

export default function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editIndustry, setEditIndustry] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/companies");
      setCompanies(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", { name, industry });
      toast.success("Client company created");
      setName("");
      setIndustry("");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    }
  };

  const openEdit = (c) => {
    setEditing(c);
    setEditName(c.name);
    setEditIndustry(c.industry || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/companies/${editing.id}`, { name: editName, industry: editIndustry });
      toast.success("Client updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const deleteCompany = async (c) => {
    const confirmed = window.prompt(
      `This permanently deletes "${c.name}" along with ALL its cycles, observations, resolutions, comments, attachments, and client users.\n\nType the company name to confirm:`
    );
    if (confirmed !== c.name) {
      if (confirmed !== null) toast.error("Name did not match — delete cancelled");
      return;
    }
    try {
      const r = await api.delete(`/companies/${c.id}`);
      toast.success(`Deleted '${c.name}' (${r.data?.cascade?.observations || 0} observations removed)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const header = (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Geist" }}>
          Clients
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage client companies under your audit firm.</p>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-company-btn">
            <Plus size={16} className="mr-1" /> New Client
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="new-company-dialog">
          <DialogHeader>
            <DialogTitle>Onboard new client company</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label>Company Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="company-name-input" />
            </div>
            <div>
              <Label>Industry (optional)</Label>
              <Input value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="company-industry-input" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="company-save-btn">
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <AppLayout header={header}>
      {loading ? (
        <div className="text-sm text-slate-500">Loading...</div>
      ) : companies.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="empty-companies">
          <Buildings size={32} className="mx-auto text-slate-400" />
          <h3 className="text-base font-semibold mt-3">No clients yet</h3>
          <p className="text-sm text-slate-500 mt-1">Create your first client to start managing audit cycles.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
          {companies.map((c) => (
            <div
              key={c.id}
              className="group relative border border-slate-200 bg-white rounded-md p-5 hover:border-blue-300 hover:shadow-sm transition"
              data-testid={`company-card-${c.id}`}
            >
              <div className="absolute top-3 right-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                      data-testid={`company-menu-${c.id}`}
                    >
                      <DotsThreeVertical size={16} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => navigate(`/audit/companies/${c.id}`)}
                      data-testid={`company-open-${c.id}`}
                    >
                      Open
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => openEdit(c)}
                      data-testid={`company-edit-${c.id}`}
                    >
                      <PencilSimple size={13} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => deleteCompany(c)}
                      className="text-red-600 focus:text-red-700"
                      data-testid={`company-delete-${c.id}`}
                    >
                      <Trash size={13} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <Link to={`/audit/companies/${c.id}`} className="block">
                <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center">
                  <Buildings size={20} weight="duotone" />
                </div>
                <h3 className="text-base font-semibold mt-3 text-slate-900">{c.name}</h3>
                <div className="text-xs text-slate-500 mt-1">{c.industry || "Industry not set"}</div>
                <div className="flex items-center gap-1 text-xs text-blue-600 mt-3 opacity-0 group-hover:opacity-100 transition">
                  Open audit console <ArrowRight size={12} />
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-company-dialog">
          <DialogHeader>
            <DialogTitle>Edit client</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} required data-testid="edit-company-name-input" />
              </div>
              <div>
                <Label>Industry</Label>
                <Input value={editIndustry} onChange={(e) => setEditIndustry(e.target.value)} data-testid="edit-company-industry-input" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="edit-company-save-btn">
                  Save changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
